import { ErrorResponse, handleFieldErrors, HandleFieldErrorsProps } from '@dmc/ui';
import axios, { AxiosError, AxiosProgressEvent, AxiosRequestConfig } from 'axios';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const INVALID_FILE_ERROR_MESSAGE = 'Invalid file!';

export interface PostFileFormModelParams {
  url: string | null;
  // An optional group of props to show server generated validation messages on specific form fields.
  serverErrorProps?: Pick<HandleFieldErrorsProps<object>, 'serverErrorFields' | 'setError'>;
}

export interface PostFileFormModel {
  errorMessage?: string;
  percentage: number;
  upload: (
    model: object,
    file?: File,
    altUrl?: string,
  ) => Promise<{ success: boolean; status?: number; data?: unknown }>;
  cancel: () => void;
}

export const usePostFileForm = ({
  url,
  serverErrorProps,
}: PostFileFormModelParams): PostFileFormModel => {
  const { t } = useTranslation();

  const [abortController, setAbortController] = useState<AbortController>(new AbortController());
  const [percentage, setPercentage] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  const onUploadProgress = useCallback((progressEvent: AxiosProgressEvent) => {
    const { loaded, total } = progressEvent;
    const percent = typeof total === 'number' ? Math.floor((loaded * 100) / total) : 0;

    if (percent <= 100) {
      setPercentage(percent);
    }
  }, []);

  const config = useMemo<AxiosRequestConfig<FormData>>(
    () => ({
      onUploadProgress,
      headers: {
        'X-CSRF': '1',
      },
      signal: abortController.signal,
    }),
    [onUploadProgress, abortController.signal],
  );

  const upload = useCallback(
    async (
      model: object,
      file?: File,
      altUrl?: string,
    ): Promise<{ success: boolean; status?: number; data?: unknown }> => {
      setErrorMessage(undefined);

      const bodyFormData = new FormData();
      bodyFormData.append('model', JSON.stringify(model));

      if (file !== undefined) {
        bodyFormData.append('file', file);
      }

      const finalUrl = altUrl ?? url;

      if (!finalUrl) {
        setErrorMessage(t('Validation.InvalidUrl'));
        return { success: false };
      }

      return await axios
        .post(finalUrl, bodyFormData, config)
        .then(res => {
          const success = res.status >= 200 && res.status < 300;
          if (success) {
            setPercentage(100);
            setErrorMessage(undefined);
          } else {
            console.debug('Error uploading: ', res.data);
            setPercentage(0);
            setErrorMessage(t('ErrorMessage.UploadError'));
          }

          setAbortController(new AbortController());

          return {
            success: success,
            status: res.status,
            data: res.data,
          };
        })
        .catch((error: Error | AxiosError) => {
          let message: string | undefined;

          if (axios.isCancel(error)) {
            message = t('ErrorMessage.UploadCancelled');
          } else if (axios.isAxiosError(error) && error.response) {
            // Handle server-side file rejection.
            if (error.response.data === INVALID_FILE_ERROR_MESSAGE) {
              message = t('ErrorMessage.InvalidFile');
            }
            // Handle server-side form field validation errors if props have been provided.
            else if (serverErrorProps) {
              const errorResponse = new ErrorResponse(error.response);
              const validationError = errorResponse.getValidationError();
              if (validationError) {
                handleFieldErrors({
                  ...serverErrorProps,
                  validationError,
                });
                message = t('Failed!') + ' ' + errorResponse.getTitle();
              }
            }
          }

          // If no error handling scenario has been matched, use the generic upload error message.
          if (typeof message === 'undefined') {
            console.debug('Exception while uploading: ', error);
            message = t('ErrorMessage.UploadError');
          }

          setErrorMessage(message);
          setPercentage(0);
          setAbortController(new AbortController());

          const status = axios.isAxiosError(error) ? error.response?.status : undefined;
          const data = axios.isAxiosError(error) ? error.response?.data : undefined;

          return { success: false, status, data };
        });
    },
    [t, config, url, serverErrorProps],
  );

  const cancel = useCallback((): void => {
    abortController.abort();
  }, [abortController]);

  return {
    errorMessage,
    percentage,
    upload,
    cancel,
  };
};

export default usePostFileForm;
